import React, { useRef, useState } from 'react';
import { Button } from "./button";
import { Loader2, Upload } from "lucide-react";
import { useToast } from "./use-toast";
import { trackActivityEvents } from '../utils/ga4Events';

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

const GPXUploadHandler = ({ onUploadComplete, compact = false }) => {
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setIsUploading(true);
    const formData = new FormData();
    let totalSizeMB = 0;
    files.forEach(file => {
      formData.append('gpx_files', file);
      totalSizeMB += file.size / (1024 * 1024);
    });

    // Track upload start
    trackActivityEvents.gpxUploadStart(files.length, totalSizeMB);

    try {
      const response = await fetch(`${API_URL}/upload_gpx`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      // Track upload success
      trackActivityEvents.gpxUploadSuccess(files.length, totalSizeMB);

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Trigger refresh of activities
      if (onUploadComplete) {
        onUploadComplete();
      }

    } catch (error) {
      trackActivityEvents.gpxUploadError(error.message);
      toast({
        title: "Error",
        description: "An error occured uploading the GPX",
        className: "bg-red-300 text-black",
        duration: 3000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  if (compact) {
    return (
      <>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".gpx"
          onChange={handleFileSelect}
          className="hidden"
        />
        <Button
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          title="Upload GPX files"
          className="h-9 px-3 flex items-center gap-2 rounded-full bg-gray-900 text-white hover:bg-gray-700 border-0"
        >
          {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          <span className="text-sm font-medium">Upload</span>
        </Button>
      </>
    );
  }

  return (
    <div className="space-y-4">
      <div className='flex justify-center'>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".gpx"
          onChange={handleFileSelect}
          className="hidden"
        />
        <Button
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="w-auto bg-gray-900 shadow-xl border-0 text-white hover:bg-gray-600 rounded-2xl"
        >
          {isUploading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" />
              Upload GPX Files
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default GPXUploadHandler;